// src/components/email/EmailDetails.jsx
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AttachmentList from './AttachmentList';
import { acme, acmeMenuPaperSx } from '../../theme/acmeTheme';
import { Card } from '../ui';

const actionIconSx = {
  color: acme.colors.brand,
  backgroundColor: acme.colors.brandMuted,
  borderRadius: acme.radius.md,
  '&:hover': {
    backgroundColor: acme.colors.brand,
    color: acme.colors.brandForeground,
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
};

const EmailDetails = ({ email, accountId, onReply, onDelete, onRelocate, availableFolders = [] }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  if (!email) return null;

  const handleRelocateClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRelocateClose = (folder) => {
    setAnchorEl(null);
    if (folder && onRelocate) {
      onRelocate(folder);
    }
  };

  const foldersToShow = availableFolders.length > 0
    ? availableFolders
    : [
        { fullPath: 'INBOX', displayName: 'Inbox' },
        { fullPath: 'Sent', displayName: 'Sent' },
        { fullPath: 'Drafts', displayName: 'Drafts' },
        { fullPath: 'Trash', displayName: 'Trash' },
      ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const cardSx = {
    p: 3,
    mb: 2,
    boxShadow: 'none',
    border: `1px solid ${acme.colors.border}`,
    backgroundColor: acme.colors.background,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: acme.colors.foreground,
                lineHeight: 1.3,
              }}
            >
              {email.subject || 'No Subject'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ color: acme.colors.brand, fontWeight: 500 }}>
                From: {email.from}
              </Typography>
              {email.date && (
                <Chip
                  label={formatDate(email.date)}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: acme.colors.border,
                    color: acme.colors.brand,
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>

            {email.to && (
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 1 }}>
                To: {email.to}
              </Typography>
            )}

            {email.unread && (
              <Chip
                label="Unread"
                size="small"
                sx={{
                  backgroundColor: acme.colors.brand,
                  color: acme.colors.brandForeground,
                  fontWeight: 500,
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => onReply(email.from, email.subject)}
              title="Reply"
              sx={actionIconSx}
            >
              <ReplyIcon />
            </IconButton>

            <IconButton
              onClick={handleRelocateClick}
              title="Move to folder"
              sx={actionIconSx}
            >
              <MoveToInboxIcon />
            </IconButton>

            <IconButton
              onClick={onDelete}
              title="Delete"
              sx={{
                color: acme.colors.destructive,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: acme.radius.md,
                '&:hover': {
                  backgroundColor: acme.colors.destructive,
                  color: '#fff',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Card sx={{ ...cardSx, flex: 1, mb: 0, overflow: 'auto' }}>
        <Box sx={{ p: 0 }}>
          <Box
            sx={{
              minHeight: '200px',
              '& img': { maxWidth: '100%', height: 'auto' },
              '& a': {
                color: acme.colors.brand,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              },
            }}
          >
            {email.html ? (
              <div
                style={{
                  margin: 0,
                  padding: 0,
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: acme.colors.foreground,
                }}
                dangerouslySetInnerHTML={{ __html: email.html }}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  color: acme.colors.foreground,
                }}
              >
                {email.text || 'No content available'}
              </Typography>
            )}
          </Box>

          {email.attachments && email.attachments.length > 0 && (
            <AttachmentList attachments={email.attachments} />
          )}
        </Box>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleRelocateClose()}
        PaperProps={{
          elevation: 0,
          sx: {
            ...acmeMenuPaperSx,
            '& .MuiMenuItem-root': {
              borderRadius: acme.radius.sm,
              margin: '2px 8px',
              '&:hover': {
                backgroundColor: acme.colors.brandMuted,
                color: acme.colors.brand,
              },
            },
          },
        }}
      >
        {foldersToShow.map((folder) => (
          <MenuItem key={folder.fullPath} onClick={() => handleRelocateClose(folder.fullPath)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              📁
              <Typography variant="body2">{folder.displayName}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default EmailDetails;
